import { LitElement, html } from "lit";

import { customElement, state } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";
import { repeat } from "lit/directives/repeat.js";

import "./notification-item";
import { notificationManagerStyles } from "./notification-manager.css";
import type { NotificationOptions } from "./notification-service";
import { NotificationService } from "./notification-service";

interface NotificationInstance {
  id: string;
  options: NotificationOptions;
}

@customElement("notification-manager")
export class NotificationManager extends LitElement {
  static override styles = notificationManagerStyles;

  @state()
  private notifications: NotificationInstance[] = [];

  @state()
  private removingNotifications = new Set<string>();

  private nextId = 1;
  private popoverContainer?: HTMLElement;

  override connectedCallback() {
    super.connectedCallback();

    // Self-initialize the notification service
    NotificationService.initialize(this);

    this.addEventListener("show-notification", this.handleShowNotification as EventListener);
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener("show-notification", this.handleShowNotification as EventListener);

    // Clean up service reference if this was the active manager
    if (NotificationService["manager"] === this) {
      NotificationService["manager"] = undefined;
    }
  }

  private handleShowNotification = (event: Event) => {
    const customEvent = event as CustomEvent<NotificationOptions>;
    const options = customEvent.detail;
    const notification: NotificationInstance = {
      id: `notification-${this.nextId++}`,
      options: {
        duration: 5000, // default
        ...options,
      },
    };

    // Add to the beginning of the array so new notifications appear at the top
    this.notifications = [notification, ...this.notifications];

    // Limit to maximum 5 notifications
    if (this.notifications.length > 5) {
      this.notifications = this.notifications.slice(0, 5);
    }

    // Show popover when first notification is added
    this.updatePopoverState();
  };

  private handleNotificationDismissed = (event: Event) => {
    const customEvent = event as CustomEvent;
    const notificationId = customEvent.detail.notificationId;

    if (notificationId && !this.removingNotifications.has(notificationId)) {
      // Phase 1: Start removal process
      this.removingNotifications.add(notificationId);
      this.removingNotifications = new Set(this.removingNotifications); // Trigger re-render

      // Phase 2: Listen for transition end to actually remove
      const element = this.shadowRoot?.querySelector(`[data-notification-id="${notificationId}"]`);
      if (element) {
        element.addEventListener(
          "transitionend",
          () => {
            this.finalizeRemoval(notificationId);
          },
          { once: true },
        );
      }
    }
  };

  private finalizeRemoval(notificationId: string) {
    // Actually remove from array after animation completes
    this.notifications = this.notifications.filter((n) => n.id !== notificationId);
    this.removingNotifications.delete(notificationId);
    this.removingNotifications = new Set(this.removingNotifications);

    // Update popover state after removal
    this.updatePopoverState();
  }

  private updatePopoverState() {
    if (!this.popoverContainer) {
      this.popoverContainer = this.shadowRoot?.querySelector(".notification-container") as HTMLElement;
    }

    if (this.popoverContainer) {
      try {
        if (this.notifications.length > 0) {
          // Force re-stacking to ensure notifications appear above modals
          this.forceRestackNotifications();
        } else {
          // Hide popover if no notifications
          if (this.popoverContainer.matches(":popover-open")) {
            this.popoverContainer.hidePopover();
          }
        }
      } catch (error) {
        // Fallback for browsers that don't support popover API
        console.warn("Popover API not supported, notifications will render in normal layer:", error);
      }
    }
  }

  private forceRestackNotifications() {
    if (!this.popoverContainer) return;

    const wasOpen = this.popoverContainer.matches(":popover-open");

    // If popover is already open, we need to re-stack it to move it above any modals
    if (wasOpen) {
      // Temporarily hide the popover
      this.popoverContainer.hidePopover();

      // Use a small delay to ensure the hide operation completes
      // then re-show to move it to the top of the top layer stack
      setTimeout(() => {
        if (this.popoverContainer && this.notifications.length > 0) {
          this.popoverContainer.showPopover();
        }
      }, 10);
    } else {
      // First time showing, just show normally
      this.popoverContainer.showPopover();
    }
  }

  override firstUpdated(_changedProperties: Map<string | number | symbol, unknown>) {
    super.firstUpdated(_changedProperties);
    // Set up popover container reference
    this.popoverContainer = this.shadowRoot?.querySelector(".notification-container") as HTMLElement;
  }

  protected override render() {
    return html`
      <div
        class=${classMap({
          "notification-container": true,
          "has-notifications": this.notifications.length > 0,
        })}
        popover="manual"
      >
        ${repeat(
          this.notifications,
          (n) => n.id,
          (notification) => html`
            <notification-item
              class="${this.removingNotifications.has(notification.id) ? "removing" : ""}"
              data-notification-id="${notification.id}"
              @notification-dismissed=${this.handleNotificationDismissed}
              .notificationId=${notification.id}
              .message=${notification.options.message}
              .severity=${notification.options.severity}
              .duration=${notification.options.duration || 5000}
            ></notification-item>
          `,
        )}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "notification-manager": NotificationManager;
  }
}
